const STORAGE_KEY = "taweel_app_zoom";
const PAN_KEY = "taweel_app_pan";
function clampZoom(z) {
  return Math.max(1, z);
}
function readZoom() {
  const z = Number(localStorage.getItem(STORAGE_KEY) || "1");
  return Number.isFinite(z) ? clampZoom(z) : 1;
}
function readPan() {
  try {
    const raw = localStorage.getItem(PAN_KEY);
    if (!raw) return { x: 0, y: 0 };
    const p = JSON.parse(raw);
    const x = Number(p.x);
    const y = Number(p.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return { x: 0, y: 0 };
    return { x, y };
  } catch {
    return { x: 0, y: 0 };
  }
}
function isInteractiveTarget(el) {
  if (!el || el === document.body) return false;
  const tag = (el.tagName || "").toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select" || tag === "button" || tag === "a") return true;
  if (el.isContentEditable) return true;
  return isInteractiveTarget(el.parentElement);
}
function ensurePanLayer() {
  let layer = document.getElementById("taweel-pan-layer");
  if (layer) return layer;
  layer = document.createElement("div");
  layer.id = "taweel-pan-layer";
  layer.setAttribute("data-taweel-pan", "1");
  Object.assign(layer.style, {
    position: "fixed",
    inset: "0",
    overflow: "hidden",
    WebkitOverflowScrolling: "touch"
  });
  const root = document.getElementById("root");
  if (root && root.parentNode) {
    const parent = root.parentNode;
    parent.insertBefore(layer, root);
    layer.appendChild(root);
  } else {
    document.body.appendChild(layer);
  }
  document.documentElement.style.overflow = "hidden";
  document.body.style.margin = "0";
  document.body.style.overflow = "hidden";
  return layer;
}
function setLayerTouchAction(layer, zoom) {
  if (!layer) return;
  layer.style.touchAction = zoom > 1 ? "none" : "auto";
}
function getViewportClampRect() {
  const vv = window.visualViewport;
  if (vv && vv.width > 0 && vv.height > 0) {
    return {
      left: vv.offsetLeft,
      top: vv.offsetTop,
      width: vv.width,
      height: vv.height
    };
  }
  return {
    left: 0,
    top: 0,
    width: window.innerWidth,
    height: window.innerHeight
  };
}
function clampPanToViewport(z, x, y) {
  if (z <= 1) return { x: 0, y: 0 };
  const root = document.getElementById("root");
  if (!root) return { x, y };
  let cx = x;
  let cy = y;
  const vb = getViewportClampRect();
  const vRight = vb.left + vb.width;
  const vBottom = vb.top + vb.height;
  const eps = 0.5;
  for (let i = 0; i < 6; i += 1) {
    root.style.transformOrigin = "0 0";
    root.style.transform = `translate(${cx}px, ${cy}px) scale(${z})`;
    const r = root.getBoundingClientRect();
    let ax = 0;
    let ay = 0;
    if (r.width > vb.width + eps) {
      const minLeft = vRight - r.width;
      const maxLeft = vb.left;
      const targetLeft = Math.min(Math.max(r.left, minLeft), maxLeft);
      ax = targetLeft - r.left;
    } else {
      ax = vb.left + (vb.width - r.width) / 2 - r.left;
    }
    if (r.height > vb.height + eps) {
      const minTop = vBottom - r.height;
      const maxTop = vb.top;
      const targetTop = Math.min(Math.max(r.top, minTop), maxTop);
      ay = targetTop - r.top;
    } else {
      ay = vb.top + (vb.height - r.height) / 2 - r.top;
    }
    cx += ax;
    cy += ay;
    if (Math.abs(ax) < 0.01 && Math.abs(ay) < 0.01) break;
  }
  return { x: cx, y: cy };
}
function applyView({ zoom, x, y }) {
  const z = clampZoom(zoom);
  const root = document.getElementById("root");
  if (!root) return { zoom: z, x, y };
  const layer = ensurePanLayer();
  setLayerTouchAction(layer, z);
  if (z === 1) {
    root.style.transform = "";
    root.style.transformOrigin = "";
    root.style.willChange = "";
    layer.scrollTo?.(0, 0);
    return { zoom: 1, x: 0, y: 0 };
  }
  root.style.transformOrigin = "0 0";
  root.style.willChange = "transform";
  let px = x;
  let py = y;
  const c = clampPanToViewport(z, px, py);
  px = c.x;
  py = c.y;
  root.style.transform = `translate(${px}px, ${py}px) scale(${z})`;
  return { zoom: z, x: px, y: py };
}
function zoomAtClientPoint({ prev, clientX, clientY, nextZoom }) {
  const root = document.getElementById("root");
  if (!root) return { ...prev, zoom: nextZoom };
  if (nextZoom <= 1) {
    return { zoom: 1, x: 0, y: 0 };
  }
  const z0 = prev.zoom;
  const z1 = nextZoom;
  const rect = root.getBoundingClientRect();
  const lx0 = (clientX - rect.left) / z0;
  const ly0 = (clientY - rect.top) / z0;
  return {
    zoom: z1,
    x: clientX - lx0 * z1,
    y: clientY - ly0 * z1
  };
}
function centerClientPointForKeyboard() {
  return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
}
function setupAppZoom() {
  if (typeof window === "undefined") return;
  const layer = ensurePanLayer();
  setLayerTouchAction(layer, readZoom());
  let currentZoom = readZoom();
  let pan = readPan();
  if (currentZoom === 1) {
    pan = { x: 0, y: 0 };
  }
  let view = applyView({ zoom: currentZoom, x: pan.x, y: pan.y });
  const wheelStep = 0.02;
  const keyStep = 0.05;
  const panWheelMult = 2.75;
  const panDragMult = 1.35;
  const persist = () => {
    currentZoom = view.zoom;
    pan = { x: view.x, y: view.y };
    localStorage.setItem(STORAGE_KEY, String(currentZoom));
    localStorage.setItem(PAN_KEY, JSON.stringify(pan));
  };
  const onWheelZoom = (e) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    e.stopPropagation();
    const dir = e.deltaY > 0 ? -1 : 1;
    const nextZ = clampZoom(view.zoom + dir * wheelStep);
    if (nextZ === 1) {
      view = applyView({ zoom: 1, x: 0, y: 0 });
    } else {
      const next = zoomAtClientPoint({ prev: view, clientX: e.clientX, clientY: e.clientY, nextZoom: nextZ });
      view = applyView(next);
    }
    persist();
  };
  const onWheelPan = (e) => {
    if (e.ctrlKey || e.metaKey) return;
    if (view.zoom <= 1) return;
    e.preventDefault();
    e.stopPropagation();
    let dx = e.deltaX;
    let dy = e.deltaY;
    if (e.deltaMode === 1) {
      dx *= 18;
      dy *= 18;
    } else if (e.deltaMode === 2) {
      dx *= 80;
      dy *= 80;
    }
    if (e.shiftKey) {
      const tmp = dx;
      dx = dy;
      dy = tmp;
    }
    view = applyView({
      zoom: view.zoom,
      x: view.x - dx * panWheelMult,
      y: view.y - dy * panWheelMult
    });
    persist();
  };
  const onKeyDown = (e) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    const k = e.key;
    if (k !== "+" && k !== "=" && k !== "-" && k !== "_" && k !== "0") return;
    e.preventDefault();
    if (k === "0") {
      view = applyView({ zoom: 1, x: 0, y: 0 });
    } else {
      const dir = k === "-" || k === "_" ? -1 : 1;
      const nextZ = clampZoom(view.zoom + dir * keyStep);
      const c = centerClientPointForKeyboard();
      if (nextZ === 1) {
        view = applyView({ zoom: 1, x: 0, y: 0 });
      } else {
        const next = zoomAtClientPoint({ prev: view, clientX: c.x, clientY: c.y, nextZoom: nextZ });
        view = applyView(next);
      }
    }
    persist();
  };
  let drag = null;
  let pinch = null;
  const touchDist = (touches) => {
    const a = touches[0];
    const b = touches[1];
    return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
  };
  const touchMid = (touches) => ({
    x: (touches[0].clientX + touches[1].clientX) / 2,
    y: (touches[0].clientY + touches[1].clientY) / 2
  });
  const MIN_PINCH_DIST = 14;
  const endPinch = () => {
    pinch = null;
    setLayerTouchAction(layer, view.zoom);
  };
  const onTouchStartPinch = (e) => {
    if (e.touches.length !== 2) return;
    const d0 = touchDist(e.touches);
    if (d0 < MIN_PINCH_DIST) return;
    pinch = { d0, z0: view.zoom };
    layer.style.touchAction = "none";
  };
  const onTouchMovePinch = (e) => {
    if (e.touches.length < 2) {
      if (pinch) endPinch();
      return;
    }
    if (!pinch) {
      const d0 = touchDist(e.touches);
      if (d0 < MIN_PINCH_DIST) return;
      pinch = { d0, z0: view.zoom };
      layer.style.touchAction = "none";
    }
    e.preventDefault();
    const d1 = touchDist(e.touches);
    if (pinch.d0 < MIN_PINCH_DIST) return;
    const nextZ = clampZoom(pinch.z0 * (d1 / pinch.d0));
    const m = touchMid(e.touches);
    const prev = view;
    if (nextZ === 1) {
      view = applyView({ zoom: 1, x: 0, y: 0 });
    } else {
      const next = zoomAtClientPoint({ prev, clientX: m.x, clientY: m.y, nextZoom: nextZ });
      view = applyView(next);
    }
    persist();
  };
  const onTouchEndPinch = (e) => {
    if (e.touches.length < 2) endPinch();
  };
  const onPointerDown = (e) => {
    if (view.zoom <= 1) return;
    if (e.button != null && e.button !== 0) return;
    if (isInteractiveTarget(e.target)) return;
    drag = {
      startX: e.clientX,
      startY: e.clientY,
      startPanX: view.x,
      startPanY: view.y,
      pointerId: e.pointerId
    };
    document.body.style.userSelect = "none";
    try {
      layer.setPointerCapture(e.pointerId);
    } catch {
    }
  };
  const onPointerMove = (e) => {
    if (!drag) return;
    if (e.pointerId !== drag.pointerId) return;
    const dx = (e.clientX - drag.startX) * panDragMult;
    const dy = (e.clientY - drag.startY) * panDragMult;
    view = applyView({ zoom: view.zoom, x: drag.startPanX + dx, y: drag.startPanY + dy });
    persist();
  };
  const onPointerUp = (e) => {
    if (!drag) return;
    if (e.pointerId !== drag.pointerId) return;
    drag = null;
    document.body.style.userSelect = "";
    try {
      layer.releasePointerCapture(e.pointerId);
    } catch {
    }
  };
  const onWheel = (e) => {
    onWheelZoom(e);
    onWheelPan(e);
  };
  window.addEventListener("wheel", onWheel, { passive: false, capture: true });
  window.addEventListener("keydown", onKeyDown, { passive: false, capture: true });
  layer.addEventListener("pointerdown", onPointerDown, { passive: true });
  layer.addEventListener("pointermove", onPointerMove, { passive: true });
  layer.addEventListener("pointerup", onPointerUp, { passive: true });
  layer.addEventListener("pointercancel", onPointerUp, { passive: true });
  layer.addEventListener("touchstart", onTouchStartPinch, { passive: true, capture: true });
  layer.addEventListener("touchmove", onTouchMovePinch, { passive: false, capture: true });
  layer.addEventListener("touchend", onTouchEndPinch, { passive: true, capture: true });
  layer.addEventListener("touchcancel", onTouchEndPinch, { passive: true, capture: true });
  const onViewportChange = () => {
    if (view.zoom <= 1) return;
    view = applyView({ zoom: view.zoom, x: view.x, y: view.y });
    persist();
  };
  window.addEventListener("resize", onViewportChange, { passive: true });
  const vv = window.visualViewport;
  if (vv) {
    vv.addEventListener("resize", onViewportChange, { passive: true });
    vv.addEventListener("scroll", onViewportChange, { passive: true });
  }
  return () => {
    window.removeEventListener("wheel", onWheel, { capture: true });
    window.removeEventListener("keydown", onKeyDown, { capture: true });
    window.removeEventListener("resize", onViewportChange);
    if (vv) {
      vv.removeEventListener("resize", onViewportChange);
      vv.removeEventListener("scroll", onViewportChange);
    }
    layer.removeEventListener("pointerdown", onPointerDown);
    layer.removeEventListener("pointermove", onPointerMove);
    layer.removeEventListener("pointerup", onPointerUp);
    layer.removeEventListener("pointercancel", onPointerUp);
    layer.removeEventListener("touchstart", onTouchStartPinch, { capture: true });
    layer.removeEventListener("touchmove", onTouchMovePinch, { capture: true });
    layer.removeEventListener("touchend", onTouchEndPinch, { capture: true });
    layer.removeEventListener("touchcancel", onTouchEndPinch, { capture: true });
  };
}
export {
  setupAppZoom
};
